import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, MapPin, Search, Filter } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubLocationsPage() {
  const { user, getAuthHeader } = useAuth();
  const [subLocations, setSubLocations] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLocation, setFilterLocation] = useState('all');
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    parent_location_id: '',
    description: ''
  });

  const fetchSubLocations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/sub-locations`, {
        headers: getAuthHeader()
      });
      setSubLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch sub-locations:', error);
      toast.error('Failed to load sub-locations');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`, {
        headers: getAuthHeader()
      });
      setLocations(response.data.filter(loc => loc.is_active !== false));
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSubLocations();
    fetchLocations();
  }, [fetchSubLocations, fetchLocations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code.trim()) {
      toast.error('Code is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!formData.parent_location_id) {
      toast.error('Parent location is required');
      return;
    }

    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        parent_location_id: formData.parent_location_id,
        description: formData.description.trim()
      };

      if (editItem) {
        await axios.patch(`${API_URL}/sub-locations/${editItem.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Sub-location updated successfully');
      } else {
        await axios.post(`${API_URL}/sub-locations`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Sub-location created successfully');
      }
      
      setOpen(false);
      resetForm();
      fetchSubLocations();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to save sub-location');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      parent_location_id: '',
      description: ''
    });
    setEditItem(null);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      parent_location_id: item.parent_location_id || '',
      description: item.description || ''
    });
    setOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/sub-locations/${selectedItem.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Sub-location deactivated');
      setOpenDelete(false);
      fetchSubLocations();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to delete sub-location');
    }
  };

  // Filter sub-locations
  const filteredSubLocations = subLocations.filter(sl => {
    const matchesSearch = 
      sl.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sl.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sl.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = filterLocation === 'all' || sl.parent_location_id === filterLocation;
    
    return matchesSearch && matchesLocation;
  });

  // Group by parent location for display
  const groupedByLocation = filteredSubLocations.reduce((acc, sl) => {
    const parentName = sl.parent_location_name || 'Unknown';
    if (!acc[parentName]) {
      acc[parentName] = [];
    }
    acc[parentName].push(sl);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MapPin className="w-6 h-6" />
              Sub-Locations Master
            </h1>
            <p className="text-muted-foreground">
              Manage exact storage positions (Racks, Shelves) within each location
            </p>
          </div>
          
          <Dialog open={open} onOpenChange={(val) => { setOpen(val); if (!val) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-sublocation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Sub-Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Edit' : 'Add'} Sub-Location</DialogTitle>
                <DialogDescription>
                  Define exact storage positions for quick item retrieval
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Code *</Label>
                    <Input
                      id="code"
                      data-testid="sublocation-code-input"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., RACK-A3-S2"
                      className="uppercase"
                    />
                    <p className="text-xs text-muted-foreground">Unique identifier</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="parent_location">Parent Location *</Label>
                    <Select 
                      value={formData.parent_location_id} 
                      onValueChange={(val) => setFormData({ ...formData, parent_location_id: val })}
                    >
                      <SelectTrigger data-testid="parent-location-select">
                        <SelectValue placeholder="Select location..." />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map(loc => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {loc.code} - {loc.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Display Name *</Label>
                  <Input
                    id="name"
                    data-testid="sublocation-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Rack A3, Shelf 2"
                  />
                  <p className="text-xs text-muted-foreground">Human-readable name for display</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Additional notes about this location..."
                    rows={2}
                  />
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-sublocation-btn">
                    {editItem ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by code, name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="sublocation-search"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filterLocation} onValueChange={setFilterLocation}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.code} - {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Badge variant="secondary">
            {filteredSubLocations.length} sub-location{filteredSubLocations.length !== 1 ? 's' : ''}
          </Badge>
        </div>

        {/* Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Parent Location</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredSubLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No sub-locations found. Click &quot;Add Sub-Location&quot; to create one.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubLocations.map((sl) => (
                  <TableRow key={sl.id} data-testid={`sublocation-row-${sl.code}`}>
                    <TableCell className="font-mono font-medium">{sl.code}</TableCell>
                    <TableCell>{sl.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {sl.parent_location_code || sl.parent_location_name || '-'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-xs truncate">
                      {sl.description || '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {sl.is_active !== false ? (
                        <Badge variant="success" className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(sl)}
                          data-testid={`edit-sublocation-${sl.code}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedItem(sl); setOpenDelete(true); }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-sublocation-${sl.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary by Location */}
        {Object.keys(groupedByLocation).length > 0 && (
          <div className="grid grid-cols-3 gap-4 mt-6">
            {Object.entries(groupedByLocation).map(([locationName, items]) => (
              <div key={locationName} className="border rounded-lg p-4">
                <h3 className="font-medium text-sm text-muted-foreground mb-2">{locationName}</h3>
                <p className="text-2xl font-bold">{items.length}</p>
                <p className="text-xs text-muted-foreground">sub-locations</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={openDelete} onOpenChange={setOpenDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Sub-Location?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate &quot;{selectedItem?.name}&quot; ({selectedItem?.code}). 
              Items using this sub-location will need to be reassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
